import gsap from "gsap";

import Flip from "gsap/Flip";
import ScrollTrigger from "gsap/ScrollTrigger";
import ScrollToPlugin from "gsap/ScrollToPlugin";
import Draggable from "gsap/Draggable";
import InertiaPlugin from "gsap/InertiaPlugin";

import mapboxgl from 'mapbox-gl';
import tippy, {followCursor} from 'tippy.js';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import PhotoSwipe from 'photoswipe';

import Splide from "@splidejs/splide";

gsap.registerPlugin(Flip, ScrollTrigger, ScrollToPlugin, Draggable, InertiaPlugin);

// --------------------------------------------------
// 🙂 General
// --------------------------------------------------

let mm = gsap.matchMedia();

const menuToggle = document.querySelector('.menu-toggle');
const menuCloseButton = document.querySelector('.menu__close');
const menuBG = document.querySelector('.menu__bg');
const menu = document.querySelector('.menu');
const logo = document.querySelector('.logo');

// --------------------------------------------------
// 👈 Back button
// --------------------------------------------------

let backButton = document.querySelector('.back-button');

// Trigger the browser back if we come from the projects page
if(backButton && sessionStorage.getItem('projects') === 'true') {
  backButton.addEventListener('click', (e) => {
    window.history.back();
    e.preventDefault();
  })
}

// Remember if we come from the projects page
if(document.documentElement.dataset.template == 'projects') {
  sessionStorage.setItem('projects', true);
}
else {
  sessionStorage.removeItem('projects');
}

// --------------------------------------------------
// 📏 Grid
// --------------------------------------------------

const grid = document.querySelector('.guides');

// Set up a tween that animates the grid in and out
let toggleGrid = gsap.to(grid, {
  duration: .5,
  ease: 'power2.inOut',
  scaleY: 1,
  autoAlpha: 1,
  paused: true
}).reverse();

// Check if the grid was enabled before
if (localStorage.getItem('debug') == 'true') {
  toggleGrid.play().progress(1);
}

// Listen for ⌃G keypresses
document.addEventListener('keydown', e => {
  if (e.key === 'g' && e.ctrlKey) {
    // Toggle the grid when ⌃G is pressed
    toggleGrid.reversed() ? toggleGrid.play() : toggleGrid.reverse();
    // Save the state in local storage
    localStorage.setItem('debug', !toggleGrid.reversed());
  }
})

// Show the window width in pixels on resize
window.addEventListener('resize', () => {
  grid.dataset.windowWidth = window.innerWidth;
})

// --------------------------------------------------
// 🌊 Menu
// --------------------------------------------------

const closeMenu = () => {

  gsap.timeline()

    .fromTo(menu, {
      yPercent: 0
    }, {
      yPercent: -120,
      ease: 'power1.inOut',
    })

    .fromTo(menuBG, {
      autoAlpha: 0.5
    }, {
      autoAlpha: 0,
      ease: 'power1.inOut',
    }, 0)

    .call(() => {
      menuToggle.setAttribute('aria-expanded', 'false');
    })

}

const openMenu = () => {

  gsap.timeline()

    .call(() => {
      menuToggle.setAttribute('aria-expanded', 'true');
    })

    .fromTo(menuBG, {
      autoAlpha: 0
    }, {
      autoAlpha: 0.5,
      ease: 'power1.inOut',
    }, 0)

    .fromTo(menu, {
      yPercent: -120,
      autoAlpha: 1
    }, {
      yPercent: 0,
      ease: 'power3.out',
    }, 0)

}

// Menu toggle
menuToggle.addEventListener('click', () => {
  let expanded = menuToggle.getAttribute('aria-expanded') === 'true';
  if(expanded) {
    closeMenu();
  }
  else {
    menuToggle.setAttribute('aria-expanded', 'true');
    openMenu();
  }
})

// Close button
menuCloseButton.addEventListener('click', () => {
  closeMenu();
})

// Esc key
document.addEventListener('keydown', e => {
  if (e.key === 'Escape' && menuToggle.getAttribute('aria-expanded') === 'true') {
    closeMenu();
  }
})

// Click outside
document.addEventListener('click', e => {
  if (menuToggle.getAttribute('aria-expanded') === 'true' && !e.target.matches('.menu, .menu *, .menu-toggle, .menu-toggle *')) {
    closeMenu();
  }
})

// --------------------------------------------------
// 💡 Lightbox
// --------------------------------------------------

let lightboxGalleries = document.querySelectorAll('.lightbox-gallery');
lightboxGalleries.forEach((lightboxGallery) => {

  const lightbox = new PhotoSwipeLightbox({
    gallery: lightboxGallery,
    children: 'a',
    showAnimationDuration: 500,
    hideAnimationDuration: 500,
    zoom: false,
    pswpModule: () => PhotoSwipe
  });

  let firstElement;
  let lastElement;

  // Captions
  lightbox.on('uiRegister', function() {
    lightbox.pswp.ui.registerElement({
      name: 'custom-caption',
      order: 9,
      isButton: false,
      appendTo: 'root',
      onInit: (el, pswp) => {
        lightbox.pswp.on('change', () => {
          const currSlideElement = lightbox.pswp.currSlide.data.element;
          let captionHTML = '';
          if (currSlideElement) {
            const imageTitle = currSlideElement.querySelector('.image-card__title');
            if (imageTitle) {
              captionHTML = imageTitle.innerHTML;
            }
          }
          if(captionHTML)  {
            el.innerHTML = captionHTML;
            el.classList.add('button')
          }
          else {
            el.innerHTML = '';
            el.classList.remove('button')
          }
        });
      }
    });
  });

  // Gallery is starting to open
  lightbox.on('afterInit', () => {
    firstElement = lightbox.pswp.currSlide.data.element
    gsap.to(firstElement.querySelector('.image-card__title'), {
      autoAlpha: 0,
      duration: .25
    })
  });

  // Gallery is closing
  lightbox.on('close', () => {
    lastElement = lightbox.pswp.currSlide.data.element
    gsap.to(lastElement.querySelector('.image-card__title'), {
      autoAlpha: 0,
      duration: .25
    })
  });

  // Gallery is closed
  lightbox.on('destroy', () => {
    gsap.to(firstElement?.querySelector('.image-card__title'), {
      autoAlpha: 1,
      ease: 'power1.inOut',
      duration: .5
    })
    gsap.to(lastElement?.querySelector('.image-card__title'), {
      autoAlpha: 1,
      ease: 'power1.inOut',
      duration: .5
    })
  });

  lightbox.init();

})

// --------------------------------------------------
// 📽️ Video hero
// --------------------------------------------------

const videoHero = document.querySelector('.video-hero');
const videoHeroTitle = document.querySelector('.video-hero__title');

if(videoHero) {

  let videoHeroAnimation = gsap.timeline({
    paused: true,
    defaults: {
      ease: 'power3.out'
    }
  })

  .fromTo(videoHeroTitle, {
    scale: .25,
  }, {
    scale: 1,
    autoAlpha: 1,
    duration: 1,
  })

  window.addEventListener('load', () => {
    videoHeroAnimation.play();
  })

  gsap.timeline({
    defaults: {
      ease: 'none'
    },
    scrollTrigger: {
      trigger: videoHero,
      start: 0,
      end: 'bottom 25%',
      scrub: 1
    }
  })

  .to(videoHero, {
    autoAlpha: 0,
  })

}

// --------------------------------------------------
// 🐭 Cursor
// --------------------------------------------------

document.addEventListener('mousemove', function(e) {

  let x = e.clientX;
  let y = e.clientY;

  // Set values to mouse position
  gsap.set('html', {'--x': x + 'px'});
  gsap.set('html', {'--y': y + 'px'});

});

// --------------------------------------------------
// Horizontal scrollers
// --------------------------------------------------

let horizontalScrollers = document.querySelectorAll('.horizontal-scroller');
let dragCursor = document.querySelector('.drag-cursor');
let dragCursorArrowLeft = dragCursor.querySelector('.drag-cursor__arrow--left');
let dragCursorArrowRight = dragCursor.querySelector('.drag-cursor__arrow--right');

let dragCursorAnimation = gsap.timeline({
  paused: true,
  defaults: {
    duration: .25,
    ease: 'power1.inOut',
  }
})

.fromTo(dragCursor, {
  autoAlpha: 0
}, {
  autoAlpha: 1
}, 0)

.fromTo(dragCursorArrowLeft, {
  xPercent: 25
}, {
  xPercent: 0
}, 0)

.fromTo(dragCursorArrowRight, {
  xPercent: -25
}, {
  xPercent: 0
}, 0)

horizontalScrollers.forEach((horizontalScroller) => {

  let viewport = horizontalScroller.querySelector('.horizontal-scroller__viewport');

  viewport.addEventListener('mouseenter', () => {
    if(horizontalScroller.classList.contains('horizontal-scroller--scrollable')) {
      dragCursorAnimation.play();
    }
  })
  viewport.addEventListener('mouseleave', () => {
    dragCursorAnimation.reverse();
  })

  // Create a scroll proxy element that handles the dragging
  let scrollProxy = document.createElement('div');
  scrollProxy.classList.add('horizontal-scroller__scroll-proxy');
  gsap.set(scrollProxy, {
    position: 'fixed',
    pointerEvents: 'none'
  })
  horizontalScroller.appendChild(scrollProxy);

  // Set up a variable outside of the scope below so we can kill it later ☠️
  let draggable;

  // Only enable draggable on desktop
  mm.add('(min-width: 1001px)', () => {

    // Set up a draggable instance
    draggable = Draggable.create(scrollProxy, {
      trigger: viewport,
      type: 'x',
      lockAxis: true,
      // cursor: false,
      inertia: true, 
      allowContextMenu: true, // Allow right click to open links
      bounds: {
        maxX: 0, 
        minX: ScrollTrigger.maxScroll(viewport, true) * -1 // Negative maximum scroll because we're scrolling left
      },
      zIndexBoost: false,
      onDragStart: function (e) {
        viewport.classList.add('horizontal-scroller__viewport--dragging');
        // dragCursorAnimation.reverse();
      },
      onDrag: function (e) {
        draggable[0]?.applyBounds({
          maxX: 0, 
          minX: ScrollTrigger.maxScroll(viewport, true) * -1 // Negative maximum scroll because we're scrolling left
        });
        viewport.scrollLeft = this.x * -1;
      },
      onThrowUpdate: function(e) {
        draggable[0]?.applyBounds({
          maxX: 0, 
          minX: ScrollTrigger.maxScroll(viewport, true) * -1 // Negative maximum scroll because we're scrolling left
        });
        viewport.scrollLeft = this.x * -1;
      },
      onThrowComplete: function(e) {
        viewport.classList.remove('horizontal-scroller__viewport--dragging');
      },
    
    });
 
    function refreshHandler() {
      draggable[0].applyBounds({
        maxX: 0, 
        minX: ScrollTrigger.maxScroll(viewport, true) * -1 // Negative maximum scroll because we're scrolling left
      });
      updateHandler();
    }
  
    function updateHandler() {
      gsap.set(scrollProxy, {x: viewport.scrollLeft * -1});
    }

    ScrollTrigger.create({
      scroller: viewport,
      horizontal: true,
      onRefresh: refreshHandler,
      onUpdate: updateHandler
    });

  });

  // Kill the throw tween when the user starts scrolling
  ScrollTrigger.observe({
    target: viewport,
    type: "wheel",
    onChange: (o) => {

      // Only do this if the draggable instance exists
      if(!draggable) return;

      // Kill the tween if the user is currently dragging
      if(viewport.classList.contains('horizontal-scroller__viewport--dragging') && draggable[0].tween) {
        viewport.classList.remove('horizontal-scroller__viewport--dragging');
        draggable[0].tween.kill();
      }

    }
  });

  const observer = new ResizeObserver(entries => {
    entries.forEach(entry => {
      const element = entry.target;
      if (element.scrollWidth > element.clientWidth) {
        horizontalScroller.classList.add('horizontal-scroller--scrollable');
        if(window.matchMedia('(min-width: 1001px)').matches) {
          draggable[0].enable();
        }
      } else {
        horizontalScroller.classList.remove('horizontal-scroller--scrollable');
        draggable[0].disable();
      }
    });
  });
  
  observer.observe(viewport);

})

// --------------------------------------------------
// 🦸‍♂️ Hero
// --------------------------------------------------

let hero = document.querySelector('.hero, .project-hero');
let heroMedia = document.querySelectorAll('.hero__media, .project-hero__media');
let heroTitle = document.querySelector('.hero__title, .project-hero__title');

if(hero) {

  let slider = hero.querySelector('.splide');
  let expandButton = hero.querySelector('.project-hero__expand, .hero__expand'); 
  let track = hero.querySelector('.splide__track');

  let expandAnimation = gsap.timeline({
    paused: true,
    defaults: {
      ease: 'back.inOut'
    }
  })

  .to('.menu-buttons', {
    yPercent: -100,
    autoAlpha: 0
  }, 0)

  .to('.logo path', {
    fill: '#fff',
    opacity: 0.5
  }, 0)

  .to('.project-hero__tags .tags, .hero__tags .tags', {
    yPercent: 100,
    autoAlpha: 0
  }, 0)

  .to(heroTitle, {
    scale: 1.05,
    autoAlpha: 0
  }, 0)

  .to(track, {
    autoAlpha: 1
  }, 0)

  expandAnimation.reverse();

  if(expandButton) {
    
    expandAnimation.to(expandButton, {
      rotate: 180
    }, 0)

    expandButton.addEventListener('click', () => {
      expandAnimation.reversed() ? expandAnimation.play() : expandAnimation.reverse();
    })

  }

  if(heroMedia.length === 1) {
    track.addEventListener('click', () => {
      expandAnimation.reversed() ? expandAnimation.play() : expandAnimation.reverse();
    })
  }
  
  if(heroMedia.length > 1) {

    hero.addEventListener('mouseenter', () => {
      dragCursorAnimation.play();
    })
  
    hero.addEventListener('mouseleave', () => {
      dragCursorAnimation.reverse();
    })

    let splide = new Splide(slider, {
      type: 'loop',
      speed: 1000,
      perPage: 1,
      arrows: true,
      pagination: false,
      pauseOnHover: false,
      arrowPath: 'M12 1L31 20L12 39'
    });


    splide.on('move', () => {
      if(expandAnimation.reversed()) {
        expandAnimation.play();
      }
    })

    splide.on('drag', () => {
      if(expandAnimation.reversed()) {
        expandAnimation.play();
      }
    })

    splide.mount();

  }

  else {

    gsap.set(slider, {
      autoAlpha: 1,
    })

  }

  let heroVideos = document.querySelectorAll('video');

  gsap.timeline({
    defaults: {
      ease: 'none'
    },
    scrollTrigger: {
      trigger: hero,
      start: 0,
      end: 'bottom 25%',
      scrub: 1,
      onLeave: () => {
        heroVideos.forEach(video => {
          video.pause();
        })
      },
      onEnterBack: () => {
        heroVideos.forEach(video => {
          video.play();
        })
      }
    },
  })

  .to(hero, {
    autoAlpha: 0,
  })

  .call(() => {
    if(!expandAnimation.reversed()) {
      expandAnimation.reverse();
    }
  })

}

// --------------------------------------------------
// 🛠️ Service blocks
// --------------------------------------------------

let servicesBlocks = document.querySelectorAll('.services-block');

servicesBlocks.forEach(servicesBlock => {

  let scroller = servicesBlock.querySelector('.services-block__scroller');
  let activeItems = servicesBlock.querySelectorAll('.icon-card--active');
  let inactiveItems = servicesBlock.querySelectorAll('.icon-card--inactive');

  // Vertical scroll animation

  gsap.timeline({
    defaults: {
      ease: 'power1.inOut'
    },
    scrollTrigger: {
      trigger: scroller,
      start: 'top bottom-=200px',
      toggleActions: 'play none none reverse',
    }
  })
  .fromTo(activeItems, {
    autoAlpha: 0,
    scale: .9,
  }, {
    autoAlpha: 1,
    scale: 1,
    stagger: .1
  })
  .fromTo(inactiveItems, {
    autoAlpha: 0,
  }, {
    autoAlpha: 1,
    duration: .5
  }, '-=.25')

})

// --------------------------------------------------
// 📰 Articles blocks
// --------------------------------------------------

let articlesBlocks = document.querySelectorAll('.articles-block');

articlesBlocks.forEach(articlesBlock => {

  let scroller = articlesBlock.querySelector('.articles-block__scroller');
  let items = articlesBlock.querySelectorAll('.card');

  // Vertical scroll animation
  gsap.timeline({
    defaults: {
      ease: 'power3.out'
    },
    scrollTrigger: {
      trigger: scroller,
      start: 'top bottom',
      end: 'top center',
      scrub: 1
    }
  })
  .fromTo(scroller, {
    xPercent: 10,
  }, {
    xPercent: 0,
  })

  // Filter
  let filter = articlesBlock.querySelector('.articles-filter');

  if(filter) {

    let filterButtons = filter.querySelectorAll('.articles-filter__button');

    const filterArticles = (filter) => {

      items.forEach(item => {

        if(filter === 'all' || item.getAttribute('data-filter') == filter) {
          gsap.set(item, {
            display: 'block'
          })
        }
        else {
          gsap.set(item, {
            display: 'none'
          })
        }

      })

      scroller.scrollLeft = 0;
      Draggable.refresh();
      ScrollTrigger.refresh();

    }

    filterButtons.forEach(filterButton => {

      filterButton.addEventListener('click', (e) => {

        filterButtons.forEach(filterButton => {
          filterButton.setAttribute('aria-pressed', 'false');
        })

        filterButton.setAttribute('aria-pressed', 'true');

        filterArticles(filterButton.getAttribute('data-filter'));

      })


    })

  }

  // Article modals
  let articleCards = articlesBlock.querySelectorAll('.article-card');
  let activeModal;
  let articleModalAnimating;

  const openArticleModal = (articleCard) => {

    let text = articleCard.querySelector('.article-card__teaser-text');
    let date = articleCard.querySelector('.article-card__date');
    let modal = document.querySelector('.modal--article[data-href="' + articleCard.getAttribute('href') + '"]');
    let modalContent = modal.querySelector('.modal__content');
    let modalBG = modal.querySelector('.modal__bg');
    
    articleModalAnimating = true;
    activeModal = modal;

    gsap.set(modalContent.querySelectorAll(':scope > *'), {
      autoAlpha: 0
    })

    gsap.set(modal, {
      autoAlpha: 1
    })

    // Animate modal
    gsap.to([text, date], {
      autoAlpha: 0,
      ease: 'power1.inOut',
      duration: .25,
      onComplete: () => {

        Flip.fit(modalContent, articleCard, {
          absolute: true
        });
    
        let modalState = Flip.getState(modalContent);
    
        gsap.set(articleCard, {
          autoAlpha: 0
        })

        gsap.set(modalContent, {
          clearProps: 'all',
          scrollTo: 0
        })
        
        Flip.from(modalState, {
          absolute: true,
          ease: 'power3.inOut',
          onComplete: () => {
            gsap.to(modalContent.querySelectorAll(':scope > *'), {
              autoAlpha: 1,
              ease: 'power1.inOut',
              duration: .25,
              onComplete: () => {
                articleModalAnimating = false;
              }
            })
          }
        });
    
        gsap.set(modalContent, {
          autoAlpha: 1,
        })
    
        gsap.to(modalBG, {
          autoAlpha: 0.75,
          ease: 'power1.inOut',
          duration: .5
        })

      }
    })

    // Update URL
    history.replaceState(null, null, articleCard.getAttribute('href'));

  }

  // Close on esc key
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && activeModal && !articleModalAnimating && !document.querySelector('.pswp--open')) {
      closeArticleModal(activeModal);
    }
  })

  // Close on click outside
  let articleModals = document.querySelectorAll('.modal--article');
  articleModals.forEach((modal) => {
    modal.addEventListener('click', (e) => {
      if(e.target.matches('.modal__close, .modal__close *, .modal__bg') && !articleModalAnimating) {
        closeArticleModal(modal);
      }
    })
  })

  const closeArticleModal = (modal) => {

    let modalContent = modal.querySelector('.modal__content');
    let modalBG = modal.querySelector('.modal__bg');
    let articleCard = document.querySelector(`.article-card[href="${modal.dataset.href}"]`);
    let text = articleCard.querySelector('.article-card__teaser-text');
    let date = articleCard.querySelector('.article-card__date');

    activeModal = null;
    articleModalAnimating = true;

    gsap.to(modalContent.querySelectorAll(':scope > *'), {
      autoAlpha: 0,
      ease: 'power1.inOut',
      duration: .25,
      onComplete: () => {

        let modalState = Flip.getState(modalContent);

        Flip.fit(modalContent, articleCard, {
          absolute: true
        });
    
        Flip.from(modalState, {
          absolute: true,
          ease: 'power3.inOut',
          onComplete: () => {

            gsap.set([text, date], {
              autoAlpha: 1,
            })

            gsap.set(articleCard, {
              autoAlpha: 1
            })

            gsap.to([modalBG, modalContent], {
              autoAlpha: 0,
              duration: .5,
              ease: 'power1.inOut',
              onComplete: () => {
                gsap.set([modalBG, modalContent], {
                  clearProps: 'all',
                })
                gsap.set(modal, {
                  autoAlpha: 0
                })
                articleModalAnimating = false;
              }
            })

          }
        });

      }
    })

    // Update URL
    history.replaceState(null, null, '#!');

  }

  articleCards.forEach(articleCard => {

    articleCard.addEventListener('click', (e) => {

      if(!articleModalAnimating) {
        openArticleModal(articleCard);
      }

      e.preventDefault();

    })

  })

  if(articleCards.length && window.location.hash) {

    articleCards.forEach(articleCard => {
      if(articleCard.getAttribute('href') === window.location.hash) {

        window.addEventListener('load', () => {
          openArticleModal(articleCard);
        })

      }
    })

  }

})

// --------------------------------------------------
// 👤 Person Modals
// --------------------------------------------------

let personCards = document.querySelectorAll('.person-card');

if(personCards.length) {

  let activeModal;
  let personModalAnimating;
  
  const openPersonModal = (personCard) => {
  
    let modal = document.querySelector('.modal--person[data-href="' + personCard.getAttribute('href') + '"]');
    let modalContent = modal.querySelector('.modal__content');
    let modalBG = modal.querySelector('.modal__bg');
    
    personModalAnimating = true;
    activeModal = modal;
  
    gsap.set(modalContent.querySelectorAll(':scope > *'), {
      autoAlpha: 0
    })

    gsap.set(modal, {
      autoAlpha: 1
    })
  
    // Animate modal
    Flip.fit(modalContent, personCard, {
      absolute: true
    });

    let modalState = Flip.getState(modalContent);

    gsap.to(personCard, {
      autoAlpha: 0,
      duration: .15
    })

    gsap.set(modalContent, {
      clearProps: 'all',
      scrollTo: 0
    })
    
    Flip.from(modalState, {
      absolute: true,
      ease: 'power3.inOut',
      onComplete: () => {
        gsap.to(modalContent.querySelectorAll(':scope > *'), {
          autoAlpha: 1,
          ease: 'power1.inOut',
          duration: .25,
          onComplete: () => {
            personModalAnimating = false;
          }
        })
      }
    });

    gsap.to(modalContent, {
      autoAlpha: 1,
      duration: .15
    })

    gsap.to(modalBG, {
      autoAlpha: 0.75,
      ease: 'power1.inOut',
      duration: .5
    })
  
    // Update URL
    history.replaceState(null, null, personCard.getAttribute('href'));
  
  }
  
  // Close on esc key
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && activeModal && !personModalAnimating && !document.querySelector('.pswp--open')) {
      closePersonModal(activeModal);
    }
  })
  
  // Close on click outside
  let personModals = document.querySelectorAll('.modal--person');
  personModals.forEach((modal) => {
    modal.addEventListener('click', (e) => {
      if(e.target.matches('.modal__close, .modal__close *, .modal__bg') && !personModalAnimating) {
        closePersonModal(modal);
      }
    })
  })
  
  const closePersonModal = (modal) => {
  
    let modalContent = modal.querySelector('.modal__content');
    let modalBG = modal.querySelector('.modal__bg');
    let personCard = document.querySelector(`.person-card[href="${modal.dataset.href}"]`);
  
    activeModal = null;
    personModalAnimating = true;
  
    gsap.to(modalContent.querySelectorAll(':scope > *'), {
      autoAlpha: 0,
      ease: 'power1.inOut',
      duration: .25,
      onComplete: () => {
  
        let modalState = Flip.getState(modalContent);
  
        Flip.fit(modalContent, personCard, {
          absolute: true
        });
    
        Flip.from(modalState, {
          absolute: true,
          ease: 'power3.inOut',
          onComplete: () => {
  
            gsap.set(personCard, {
              autoAlpha: 1
            })
  
            gsap.to([modalBG, modalContent], {
              autoAlpha: 0,
              duration: .5,
              ease: 'power1.inOut',
              onComplete: () => {
                gsap.set([modalBG, modalContent], {
                  clearProps: 'all',
                })
                gsap.set(modal, {
                  autoAlpha: 0
                })
                personModalAnimating = false;
              }
            })
  
          }
        });
  
      }
    })
  
    // Update URL
    history.replaceState(null, null, '#!');
  
  }
  
  personCards.forEach(personCard => {
  
    personCard.addEventListener('click', (e) => {
  
      if(!personModalAnimating) {
        openPersonModal(personCard);
      }
  
      e.preventDefault();
  
    })
  
  })
  
  if(personCards.length && window.location.hash) {
  
    personCards.forEach(personCard => {
      if(personCard.getAttribute('href') === window.location.hash) {
  
        window.addEventListener('load', () => {
          openPersonModal(personCard);
        })
  
      }
    })
  
  }

}

// --------------------------------------------------
// 🛠️ Service Modals
// --------------------------------------------------

let serviceCards = document.querySelectorAll('.service-card, .icon-card__link');

if(serviceCards.length) {

  let activeModal;
  let serviceModalAnimating;
  
  const openServiceModal = (serviceCard) => {
  
    let modal = document.querySelector('.modal--service[data-href="' + serviceCard.getAttribute('href') + '"]');
    let modalContent = modal.querySelector('.modal__content');
    let modalBG = modal.querySelector('.modal__bg');
    
    serviceModalAnimating = true;
    activeModal = modal;
  
    gsap.set(modalContent.querySelectorAll(':scope > *'), {
      autoAlpha: 0,
    })

    gsap.set(modal, {
      autoAlpha: 1
    })
  
    // Animate modal
    Flip.fit(modalContent, serviceCard, {
      absolute: true
    });

    let modalState = Flip.getState(modalContent);

    gsap.to(serviceCard, {
      autoAlpha: 0,
      duration: .15
    })

    gsap.set(modalContent, {
      clearProps: 'all',
      scrollTo: 0
    })
    
    Flip.from(modalState, {
      absolute: true,
      ease: 'power3.inOut',
      onComplete: () => {
        gsap.to(modalContent.querySelectorAll(':scope > *'), {
          autoAlpha: 1,
          ease: 'power1.inOut',
          duration: .25,
          onComplete: () => {
            serviceModalAnimating = false;
          }
        })
      }
    });

    gsap.to(modalContent, {
      autoAlpha: 1,
      duration: .15
    })

    gsap.to(modalBG, {
      autoAlpha: 0.75,
      ease: 'power1.inOut',
      duration: .5
    })
  
    // Update URL
    history.replaceState(null, null, serviceCard.getAttribute('href'));
  
  }
  
  // Esc key
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && activeModal && !serviceModalAnimating && !document.querySelector('.pswp--open')) {
      closeServiceModal(activeModal);
    }
  })
  
  let serviceModals = document.querySelectorAll('.modal--service');
  serviceModals.forEach((modal) => {
    modal.addEventListener('click', (e) => {
      if(e.target.matches('.modal__close, .modal__close *, .modal__bg') && !serviceModalAnimating) {
        closeServiceModal(modal);
      }
    })
  })
  
  const closeServiceModal = (modal) => {
  
    let modalContent = modal.querySelector('.modal__content');
    let modalBG = modal.querySelector('.modal__bg');
    let serviceCard = document.querySelector(`.service-card[href="${modal.dataset.href}"], .icon-card__link[href="${modal.dataset.href}"]`);
  
    activeModal = null;
    serviceModalAnimating = true;
  
    gsap.to(modalContent.querySelectorAll(':scope > *'), {
      autoAlpha: 0,
      ease: 'power1.inOut',
      duration: .25,
      onComplete: () => {
  
        let modalState = Flip.getState(modalContent);
  
        Flip.fit(modalContent, serviceCard, {
          absolute: true
        });
    
        Flip.from(modalState, {
          absolute: true,
          ease: 'power3.inOut',
          onComplete: () => {
  
            gsap.set(serviceCard, {
              autoAlpha: 1
            })
  
            gsap.to([modalBG, modalContent], {
              autoAlpha: 0,
              duration: .5,
              ease: 'power1.inOut',
              onComplete: () => {
                gsap.set([modalBG, modalContent], {
                  clearProps: 'all',
                })
                gsap.set(modal, {
                  autoAlpha: 0
                })
                serviceModalAnimating = false;
              }
            })
  
          }
        });
  
      }
    })
  
    // Update URL
  
    history.replaceState(null, null, '#!');
  
  }
  
  serviceCards.forEach(serviceCard => {
  
    serviceCard.addEventListener('click', (e) => {
  
      if(!serviceModalAnimating) {
        openServiceModal(serviceCard);
      }
  
      e.preventDefault();
  
    })
  
  })
  
  // Open modal if URL contains hash
  if(serviceCards.length && window.location.hash) {
  
    serviceCards.forEach(serviceCard => {
      if(serviceCard.getAttribute('href') === window.location.hash) {
  
        window.addEventListener('load', () => {
          openServiceModal(serviceCard);
        })
  
      }
    })
  
  }

}

// --------------------------------------------------
// Projects Map
// --------------------------------------------------

let projectsMap = document.querySelector('#projects-map');

if(projectsMap) {

  let projectsMapWrap = document.querySelector('.projects-map__wrap');
  let projectsMapButtonsWrap = document.querySelector('.projects-map__buttons');
  let projectsMapButtons = document.querySelectorAll('.projects-map__button');

  mapboxgl.accessToken = 'pk.eyJ1IjoibWVkaWVuYmFlY2tlciIsImEiOiJjam9pZWJlcjMwMHY3M3BvZHhjZG1zcmh6In0.wnwlAFl1v0CK3oM2bpqHfQ';
  const map = new mapboxgl.Map({
    container: projectsMap,
    style: 'mapbox://styles/medienbaecker/clj8b32on003701o0c0ib8opj',
    center: [8.56902, 48.528577],
    zoom: 5,
    scrollZoom: true,
    dragRotate: false,
    logoPosition: 'bottom-right',
    localFontFamily: 'DIN Pro',
    pitch: 60, // pitch in degrees
    bearing: -60, // bearing in degrees
  })

  // Disable zoom on scroll without ctrl, meta or alt key
  map.on("wheel", event => {
    if (event.originalEvent.ctrlKey) return;
    if (event.originalEvent.metaKey) return;
    if (event.originalEvent.altKey) return;
    event.preventDefault();
  });

  map.on('load', () => {

    map.addSource('projects', {
      type: 'geojson',
      data: document.documentElement.dataset.url + '/locations.json',
      cluster: true,
      clusterMaxZoom: 14, // Max zoom to cluster points on
      clusterRadius: 30, // Radius of each cluster when clustering points (defaults to 50)
    });

    // Cluster circles
    map.addLayer({
      id: 'cluster',
      type: 'circle',
      source: 'projects',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': '#232323',
        'circle-radius': 15,
      }
    });

    // Cluster count labels
    map.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: 'projects',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Pro Bold', 'Arial Unicode MS Bold'],
        'text-size': 15,
        'text-allow-overlap': true,
      },
      paint: {
        'text-color': '#fff',
      }
    });

    // Unclustered point circles
    map.addLayer({
      id: 'unclustered-point',
      type: 'circle',
      source: 'projects',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': '#fff',
        'circle-radius': 10,
        'circle-stroke-width': 5,
        'circle-stroke-color': 'transparent'
      }
    });

  })

  // Click on a cluster
  map.on('click', 'cluster', (e) => {
    
    let features = map.queryRenderedFeatures(e.point, {
      layers: ['cluster']
    });

    let clusterId = features[0].properties.cluster_id;
    map.getSource('projects').getClusterExpansionZoom(clusterId, (err, zoom) => {
      if (err) return;

      map.easeTo({
        center: features[0].geometry.coordinates,
        zoom: zoom
      });

    });

  });

  let tippyInstance = tippy(projectsMap, {
    content: null,
    plugins: [followCursor],
    followCursor: true,
    arrow: false,
    placement: 'bottom',
    theme: 'pro-cycl',
    trigger: 'manual',
    maxWidth: null
  })

  map.on('mouseleave', 'unclustered-point', () => {
    tippyInstance.hide();
  })
  map.on('mouseenter', 'unclustered-point', (e) => {
    tippyInstance.setContent(e.features[0].properties.title);
    tippyInstance.show();
  })

  map.on('mouseleave', 'cluster', () => {
    tippyInstance.hide();
  })
  map.on('mouseenter', 'cluster', (e) => {
    tippyInstance.setContent(e.features[0].properties.point_count_abbreviated + ' Destinationen');
    tippyInstance.show();
  })

  // Check if the mouse is over a cluster or a project
  map.on('mousemove', (e) => {
    let features = map.queryRenderedFeatures(e.point, {
      layers: ['cluster', 'unclustered-point']
    });
    if(features.length) {
      map.getCanvas().style.cursor = 'pointer';
    }
    else {
      map.getCanvas().style.cursor = '';
    }
  })

  // Click on a project
  map.on('click', 'unclustered-point', (e) => {

    let features = map.queryRenderedFeatures(e.point, {
      layers: ['unclustered-point']
    });

    // Scroll to the projects section
    window.scrollTo({
      top: document.querySelector('.projects').offsetTop - 50,
      behavior: 'smooth'
    })

    // Filter projects
    let servicesSelect = document.querySelector('[data-filter="services"]');
    servicesSelect.value = 'all';

    let locationsSelect = document.querySelector('[data-filter="location"]');
    locationsSelect.value = features[0].properties.title;
    locationsSelect.dispatchEvent(new Event('change'));

  });

  let mapLoaded = false;

  map.on('data', (e) => {

    console.log(e, e.sourceId);

    if(mapLoaded || e.sourceId !== 'projects' || !e.isSourceLoaded) return;

    let renderedFeatures = map.queryRenderedFeatures({
      layers: ['cluster', 'unclustered-point']
    });
  
    if(renderedFeatures.length === 0) return;

    let bounds = new mapboxgl.LngLatBounds();
  
    renderedFeatures.forEach(feature => {
      bounds.extend(feature.geometry.coordinates);
    })
  
    map.fitBounds(bounds, {
      padding: {
        top: 100,
        bottom: 150,
        left: 50,
        right: 50
      },
      animate: false
    });

  })

  let mapLoadAnimation = gsap.timeline({
    paused: true,
    defaults: {
      ease: 'power1.inOut',
    }
  })

  .to(projectsMap, {
    autoAlpha: 1,
    duration: .5
  })

  .fromTo(projectsMapButtonsWrap, {
    xPercent: 200,
  },{
    xPercent: 0,
    autoAlpha: 1,
    ease: 'back.out'
  })

  map.once('idle', (e) => {
    mapLoaded = true;
    mapLoadAnimation.play();
  });

  gsap.timeline({
    defaults: {
      ease: 'none'
    },
    scrollTrigger: {
      trigger: projectsMap,
      start: 0,
      end: 'bottom 25%',
      scrub: 1
    }
  })

  .to(projectsMapWrap, {
    autoAlpha: 0,
  })

   // Plus/Minus buttons
   projectsMapButtons.forEach(button => {
    button.addEventListener('click', () => {
      if(button.classList.contains('projects-map__button--plus')) {
        map.easeTo({
          zoom: map.getZoom() + .5
        })
      }
      else {
        map.easeTo({
          zoom: map.getZoom() - .5
        })
      }
    })
  })
  
  const projectCards = document.querySelectorAll('.project-card');

  // Empty check
  const checkEmpty = () => {

    let visibleProjects = 0;

    projectCards.forEach(project => {
      if(gsap.getProperty(project, 'display') === 'block') {
        visibleProjects++;
      }
    })

    if(visibleProjects) {
      gsap.set('.projects__empty', {
        display: 'none'
      })
    } else {
      gsap.set('.projects__empty', {
        display: 'block'
      })
    }

  }

  let projectCardsState = null;
  let projectsCardsWrapperStartHeight = null;
  let projectsCardsWrapperEndHeight = null;

  const saveProjectsCardsState = () => {
    projectsCardsWrapperStartHeight = gsap.getProperty('.projects__items', 'height');
    projectCardsState = Flip.getState([projectCards, '.projects__empty']);
  }

  const animateProjectsCardsState = () => {

    projectsCardsWrapperEndHeight = gsap.getProperty('.projects__items', 'height');

    let flip = Flip.from(projectCardsState, {
      ease: 'power2.inOut',
      absolute: true,
      onEnter: elements => gsap.fromTo(elements, {opacity: 0, scale: 0}, {opacity: 1, scale: 1, ease: 'power2.inOut'}),
      onLeave: elements => gsap.fromTo(elements, {opacity: 1, scale: 1}, {opacity: 0, scale: 0, ease: 'power2.inOut'})
    })

    flip.fromTo('.projects__items', {
      height: projectsCardsWrapperStartHeight
    }, {
      height: projectsCardsWrapperEndHeight,
      ease: 'power2.inOut',
      clearProps: "height",
    }, 0)

  }

  /* -------------------------------------------------- */
  /* 🚴 Projects Filter */
  /* -------------------------------------------------- */

  const filterSelects = document.querySelectorAll('.projects-filter__select');
  const filterAttributes = [];
  let activeFilters = [];

  const disableUnavailableOptions = () => {

    availableOptions = [];

    projectCards.forEach(project => {

      filterAttributes.forEach(filterAttribute => {

        if(gsap.getProperty(project, 'display') === 'none' && activeFilters[0] !== filterAttribute) {
          return;
        }

        let filterAttributeValue = project.getAttribute('data-' + filterAttribute);

        if(filterAttributeValue) {

          let filterAttributeValues = filterAttributeValue.split(';');

          filterAttributeValues.forEach(filterAttributeValue => {

            if(!availableOptions[filterAttribute]) {
              availableOptions[filterAttribute] = [];
            }

            if(!availableOptions[filterAttribute].includes(filterAttributeValue)) {
              availableOptions[filterAttribute].push(filterAttributeValue);
            }

          })

        }

      })

    })

    // Iterate over each filter select element
    filterSelects.forEach(filterSelect => {
      
      // Check if there is only one active filter
      if(activeFilters.length === 1) {
        // Check if the current filter select is the active filter
        if(activeFilters.includes(filterSelect.dataset.filter)) {
          return;
        }
      }
      
      // Get the filter attribute of the current filter select
      let filter = filterSelect.dataset.filter;

      // Iterate over each option in the filter select
      filterSelect.querySelectorAll('option').forEach(option => {

        let optionValue = option.value;

        // Check if the option value is 'all' or included in the available options for the filter attribute
        if(optionValue === 'all' || availableOptions[filter]?.includes(optionValue)) {
          // Enable the option
          option.disabled = false;
        }
        else {
          // Disable the option
          option.disabled = true;
        }

      })

    })

  }

  const performFilter = (filterSelect, animation = true) => {
      
    let filter = filterSelect.dataset.filter;
    let filterValue = filterSelect.value;

    saveProjectsCardsState();

    if(filterValue === 'all') {
      activeFilters = activeFilters.filter(item => item !== filter);
    }
    else {
      activeFilters = activeFilters.filter(item => item !== filter);
      activeFilters.push(filter);
    }

    projectCards.forEach(project => {

      let filterAttribute = project.getAttribute('data-' + filter);
      
      let filterAttributes = [];

      if(filterAttribute) {
        filterAttributes = filterAttribute.split(';');
      }

      let filtered = true;
      if(filterValue === 'all') {
        filtered = false;
      }

      filterAttributes.forEach(filterAttribute => {
        if(filterAttribute == filterValue) {
          filtered = false;
        }
      })

      if(filtered) {
        project.classList.add('project-card--hidden-by-' + filter)
      }
      else {
        project.classList.remove('project-card--hidden-by-' + filter)
      }

    })

    disableUnavailableOptions();

    checkEmpty();

    if(animation) {
      animateProjectsCardsState();
    }

    updateUrl();

  }

  filterSelects.forEach(filterSelect => {

    filterAttributes.push(filterSelect.dataset.filter);

    filterSelect.addEventListener('change', () => {

      performFilter(filterSelect);

    })

  })

  disableUnavailableOptions();

  const updateUrl = () => {

    let url = window.location.origin + window.location.pathname;

    activeFilters.forEach((filter, index) => {

      let filterSelect = document.querySelector(`.projects-filter__select[data-filter="${filter}"]`);
      let filterValue = filterSelect.querySelector('option:checked').dataset.slug;

      if(index === 0) {
        url += '#' + filterValue;
      }
      else {
        url += '+' + filterValue;
      }

    })

    history.replaceState(null, null, url);

  }

  window.addEventListener('load', () => {

    // Check for hash and select the corresponding options
    if(window.location.hash) {
        
      try {

        let hashFilters = window.location.hash.replace('#', '').split('+');

        let optionsWithHash = [];

        hashFilters.forEach(hashFilter => {
          let option = document.querySelector(`.projects-filter__select option[data-slug="${hashFilter}"]`)
          if(option) {
            optionsWithHash.push(option);
          }
        })

        if(optionsWithHash.length) {

          // Scroll to the projects section
          window.scrollTo({
            top: document.querySelector('.projects').offsetTop - 50,
            behavior: 'smooth'
          })

          optionsWithHash.forEach(optionWithHash => {

            // Select the option
            optionWithHash.selected = true;

            performFilter(optionWithHash.closest('select'), false);

          })

        }

      }
      catch(e) {
        console.log(e);
      }

    }

  })

  // Search
  const searchInput = document.querySelector('.projects-filter__input');
  let timeoutId;

  searchInput.addEventListener('input', () => {

    clearTimeout(timeoutId);

    timeoutId = setTimeout(() => {
      let searchValue = searchInput.value.toLowerCase();

      saveProjectsCardsState();

      projectCards.forEach(project => {

        let projectText = '';
        let projectTitle = project.querySelector('.card__button span').innerHTML.toLowerCase();
        let projectHiddenText = project.querySelector('.project-card__hidden-text').innerHTML.toLowerCase();

        projectText = projectTitle + ' ' + projectHiddenText;

        if(projectText.includes(searchValue)) {
          project.classList.remove('project-card--hidden-by-search')
        } else {
          project.classList.add('project-card--hidden-by-search')
        }

      })

      disableUnavailableOptions();
      checkEmpty();

      animateProjectsCardsState();
    }, 250);

  })


}